import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { BrandingProvider } from "./context/BrandingContext";
import axios from "axios";
import { initNativeShell, isNative, getPlatform, registerNativePush } from "./native";
import { OfflineBanner } from "./components/OfflineBanner";
import { WebSocketProvider } from "./hooks/useWebSocket";
import { LanguageProvider } from "./i18n/LanguageContext";
import { Layout } from "./components/Layout";
import { InstallPrompt } from "./components/InstallPrompt";
import { PWAInstallPrompt } from "./components/PWAInstallPrompt";
import { Home } from "./pages/Home";
import { Login } from "./pages/Login";
import { Register } from "./pages/Register";
import { Dashboard } from "./pages/Dashboard";
import { Tournaments } from "./pages/Tournaments";
import { CreateTournament } from "./pages/CreateTournament";
import { DiscoverTournaments } from "./pages/DiscoverTournaments";
import { TournamentMap } from "./pages/TournamentMap";
import { Teams } from "./pages/Teams";
import { CreateTeam } from "./pages/CreateTeam";
import { Games } from "./pages/Games";
import { Predictions } from "./pages/Predictions";
import { Leaderboard } from "./pages/Leaderboard";
import { TeamComparison } from "./pages/TeamComparison";
import { PaymentSuccess, PaymentCancel } from "./pages/PaymentResult";
import { FreeAgents } from "./pages/FreeAgents";
import { Trades } from "./pages/Trades";
import { Favorites } from "./pages/Favorites";
import { Notifications } from "./pages/Notifications";
import { Leagues } from "./pages/Leagues";
import { CreateLeague } from "./pages/CreateLeague";
import { OfficialPortal } from "./pages/OfficialPortal";
import { SearchTournaments } from "./pages/SearchTournaments";
import { AdminDashboard } from "./pages/AdminDashboard";
import { TournamentBracket } from "./pages/TournamentBracket";
import { TeamManagement } from "./pages/TeamManagement";
import { PoolPlay } from "./pages/PoolPlay";
import { RosterManagement } from "./pages/RosterManagement";
import { PerformanceCharts } from "./pages/PerformanceCharts";
import { PlayerLeaderboard } from "./pages/PlayerLeaderboard";
import { Archives } from "./pages/Archives";
import { TournamentTemplates } from "./pages/TournamentTemplates";
import { SMSSettings } from "./pages/SMSSettings";
import { BrandingSettings } from "./pages/BrandingSettings";
import { RefereeDashboard } from "./pages/RefereeDashboard";
import { TeamAnalytics } from "./pages/TeamAnalytics";
import { EmbedWidget } from "./pages/EmbedWidget";
import { RefereeAssignment } from "./pages/RefereeAssignment";
import { BracketBuilder } from "./pages/BracketBuilder";
import { TournamentDirector } from "./pages/TournamentDirector";
import { RefereeCalendar } from "./pages/RefereeCalendar";
import { TournamentDetails } from "./pages/TournamentDetails";
import { FollowedTeams } from "./pages/FollowedTeams";
import { LeagueLanding } from "./pages/LeagueLanding";
import { PlayerJoin } from "./pages/PlayerJoin";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Applies native status bar / splash screen handling on device, keeping it
// in sync with the current theme, and registers the device for native push
// notifications once a user is signed in. No-op on web.
const NativeShell = () => {
  const { theme } = useTheme();
  const { user } = useAuth();

  useEffect(() => {
    initNativeShell(theme === 'dark');
  }, [theme]);

  useEffect(() => {
    if (!user || !isNative()) return;
    registerNativePush((token) => {
      axios.post(`${API}/push/register-device`, { token, platform: getPlatform() })
        .catch((err) => console.error('Failed to register device token:', err));
    });
  }, [user]);

  return null;
};

// App Routes
const AppRoutes = () => {
  return (
    <Layout>
      <NativeShell />
      <OfflineBanner />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/tournaments" element={<Tournaments />} />
        <Route path="/search" element={<SearchTournaments />} />
        <Route path="/discover" element={<DiscoverTournaments />} />
        <Route path="/map" element={<TournamentMap />} />
        <Route path="/teams" element={<Teams />} />
        <Route path="/games" element={<Games />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/free-agents" element={<FreeAgents />} />
        <Route path="/trades" element={<Trades />} />
        <Route path="/leagues" element={<Leagues />} />
        <Route path="/league/:slug" element={<LeagueLanding />} />
        <Route path="/join/:inviteCode" element={<PlayerJoin />} />
        <Route path="/official-portal" element={<OfficialPortal />} />
        <Route path="/tournaments/:tournamentId" element={<TournamentDetails />} />
        <Route path="/tournaments/:tournamentId/bracket" element={<TournamentBracket />} />
        
        {/* Protected Routes */}
        <Route path="/admin" element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        } />
        <Route path="/teams/:teamId/manage" element={
          <ProtectedRoute>
            <TeamManagement />
          </ProtectedRoute>
        } />
        <Route path="/tournaments/:tournamentId/pools" element={<PoolPlay />} />
        <Route path="/teams/:teamId/roster" element={
          <ProtectedRoute>
            <RosterManagement />
          </ProtectedRoute>
        } />
        <Route path="/performance" element={<PerformanceCharts />} />
        <Route path="/teams/:teamId/performance" element={<PerformanceCharts />} />
        <Route path="/compare" element={<TeamComparison />} />
        <Route path="/leaderboard/players" element={<PlayerLeaderboard />} />
        <Route path="/archives" element={<Archives />} />
        <Route path="/templates" element={
          <ProtectedRoute>
            <TournamentTemplates />
          </ProtectedRoute>
        } />
        <Route path="/settings/sms" element={
          <ProtectedRoute>
            <SMSSettings />
          </ProtectedRoute>
        } />
        <Route path="/settings/branding" element={
          <ProtectedRoute>
            <BrandingSettings />
          </ProtectedRoute>
        } />
        <Route path="/referee" element={
          <ProtectedRoute>
            <RefereeDashboard />
          </ProtectedRoute>
        } />
        <Route path="/referee/calendar" element={
          <ProtectedRoute>
            <RefereeCalendar />
          </ProtectedRoute>
        } />
        <Route path="/teams/:teamId/analytics" element={
          <ProtectedRoute>
            <TeamAnalytics />
          </ProtectedRoute>
        } />
        <Route path="/tournaments/:tournamentId/embed" element={
          <ProtectedRoute>
            <EmbedWidget />
          </ProtectedRoute>
        } />
        <Route path="/tournaments/:tournamentId/referees" element={
          <ProtectedRoute>
            <RefereeAssignment />
          </ProtectedRoute>
        } />
        <Route path="/tournaments/:tournamentId/bracket-builder" element={
          <ProtectedRoute>
            <BracketBuilder />
          </ProtectedRoute>
        } />
        <Route path="/tournaments/:tournamentId/director" element={
          <ProtectedRoute>
            <TournamentDirector />
          </ProtectedRoute>
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/tournaments/create" element={
          <ProtectedRoute>
            <CreateTournament />
          </ProtectedRoute>
        } />
        <Route path="/leagues/create" element={
          <ProtectedRoute>
            <CreateLeague />
          </ProtectedRoute>
        } />
        <Route path="/teams/create" element={
          <ProtectedRoute>
            <CreateTeam />
          </ProtectedRoute>
        } />
        <Route path="/predictions" element={
          <ProtectedRoute>
            <Predictions />
          </ProtectedRoute>
        } />
        <Route path="/favorites" element={
          <ProtectedRoute>
            <Favorites />
          </ProtectedRoute>
        } />
        <Route path="/following/teams" element={
          <ProtectedRoute>
            <FollowedTeams />
          </ProtectedRoute>
        } />
        <Route path="/notifications" element={
          <ProtectedRoute>
            <Notifications />
          </ProtectedRoute>
        } />
        
        {/* Payment Result Routes */}
        <Route path="/payment/success" element={
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        } />
        <Route path="/payment/cancel" element={
          <ProtectedRoute>
            <PaymentCancel />
          </ProtectedRoute>
        } />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
};

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <BrandingProvider>
            <WebSocketProvider>
              <BrowserRouter>
                <AppRoutes />
                <PWAInstallPrompt />
                <Toaster position="top-right" richColors />
              </BrowserRouter>
            </WebSocketProvider>
          </BrandingProvider>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
